import queryString from 'query-string';
import { useCallback, useState, type SetStateAction } from 'react';
import { useDispatch } from 'react-redux';

import { quotePartViewer } from 'modules/quotePartViewer/data/actions';

//PartSetupState is used for a bitmask of the part setup tool's state
//Bit 1 is for mouse or keyboard. It captures the last state change was made by mouse or keyboard. (0 == mouse, 1 === keyboard)
//Bit 2 is for gotoLast
//Bits 3-4 is for Tab Index. (Part Fields only; Found in Files moved to separate app)
//Could easily add more bits in future for Tab Index. For now reserving 2 bits, for a total of 4 tabs

const mouseOrKeyboardMask = 0b1; //bit 1. (0 === mouse, 1 === keyboard)
const gotoLastMask = 0b10; //bit 2 gotoLast state (1 === true, 0 === false)
const gotoLastShift = 1; //gotoLast starts at bit 2

export const enum PartSetupStateChangedBy {
  Undefined = 0,
  Mouse = 1,
  Keyboard = 2,
}

export type PartSetupState = {
  quoteItemId: number;
  lastChangedBy: PartSetupStateChangedBy;
  gotoLast: boolean;
  partSetupState: string; //encodes tabIndex, lastChangedBy and gotoLast as a bitmask base 36 encoded
};

export type PartSetupStateChanges = Partial<
  Omit<PartSetupState, 'partSetupState'>
>;

const wasLastSetByMouse = (bitmask: number): boolean =>
  (bitmask & mouseOrKeyboardMask) === 0;

const wasLastSetByKeyboard = (bitmask: number): boolean =>
  (bitmask & mouseOrKeyboardMask) === 0b1;

const wasGotoLast = (bitmask: number): boolean =>
  (bitmask & gotoLastMask) >> gotoLastShift === 0b1;

const encodePartSetupState = ({
  lastChangedBy = PartSetupStateChangedBy.Mouse,
  gotoLast = false,
}: Pick<PartSetupStateChanges, 'lastChangedBy' | 'gotoLast'>): string =>
  (Math.max(0, lastChangedBy - 1) | (gotoLast ? 0b10 : 0b00)).toString(36); //base 36 encoded

export const buildPartSetupState = ({
  lastChangedBy = PartSetupStateChangedBy.Mouse,
  gotoLast = false,
}: Pick<PartSetupStateChanges, 'lastChangedBy' | 'gotoLast'>): string =>
  encodePartSetupState({ lastChangedBy, gotoLast });

export const decodePartSetupState = (
  search: string = window.location.search,
): PartSetupState => {
  const state = queryString.parse(search);
  const quoteItemId = parseInt(String(state.quote_item_id ?? ''), 10);
  const partSetupState = String(state.part_setup ?? '0');
  const partSetupStateBitmask: number = parseInt(partSetupState, 36); //base 36 encoded
  const lastChangedBy = wasLastSetByMouse(partSetupStateBitmask)
    ? PartSetupStateChangedBy.Mouse
    : wasLastSetByKeyboard(partSetupStateBitmask)
    ? PartSetupStateChangedBy.Keyboard
    : PartSetupStateChangedBy.Undefined;
  const gotoLast = wasGotoLast(partSetupStateBitmask);
  return { quoteItemId, lastChangedBy, gotoLast, partSetupState };
};

//usePartSetupState: Used to observe and make changes to partSetupState
//* maintains state in `partSetupState` and the URL
//* Only makes changes to state if there are real changes
export const usePartSetupState = (): [
  PartSetupState,
  SetStateAction<PartSetupStateChanges>,
] => {
  const dispatch = useDispatch();
  const [currentState, setCurrentState] =
    useState<PartSetupState>(decodePartSetupState);

  const setPartSetupState = useCallback(
    (changes: SetStateAction<PartSetupStateChanges>) => {
      if (typeof changes === 'function') {
        //eslint-disable-next-line no-param-reassign
        changes = changes(currentState);
      }
      //merge currentState & changes, and update encoding of partSetupState
      //IMPORTANT: Always use decodePartSetupState() because it may be more up to date
      //than `currentState` because of a usePartSetupState() in another component changing url
      //It is okay that `currentState` is outdated temporarily. It will be brought up to date
      //when the `lastChangedBy` is updated.
      // NOTE: as of 8/27/24, this is only called in a react context and should not be
      //  called in multiple places
      const nextState: PartSetupState = {
        ...decodePartSetupState(),
        ...changes,
      };
      nextState.partSetupState = encodePartSetupState(nextState);

      const { quoteItemId, partSetupState, gotoLast } = nextState;
      if (quoteItemId !== currentState.quoteItemId) {
        dispatch(
          quotePartViewer.receiveQuoteItemId({
            quoteItemId,
            partSetupState,
            gotoLast,
          }),
        );
      }
      if (
        quoteItemId !== currentState.quoteItemId ||
        partSetupState !== currentState.partSetupState //encodes both gotoLast, lastChangedBy and tabIndex
      ) {
        setCurrentState(nextState);
        //Only if there is a change, update URL and update _setPartSetupState
        //Update history. Yes, intentionally using `window.history`!
        //* Warning DO NOT use `const { push } = useHistory()` or `import history from 'utils/browserHistory'` because it causes
        //a full page reload and we loose quoteItemId changes and saga that watches `types.RECEIVE_QUOTE_ITEM_ID` doesn't
        //execute properly. We don't want to unmount the viewer and start loading everything from scratch.
        const {
          history,
          location: { pathname },
        } = window;
        history.pushState(
          history.state ?? {},
          '',
          `${pathname}?${queryString.stringify({
            quote_item_id: quoteItemId,
            part_setup: partSetupState,
          })}`,
        );
        return nextState;
      }
      return currentState;
    },
    [dispatch, currentState],
  );

  return [currentState, setPartSetupState];
};
